import { useState, useEffect, useCallback, useRef } from 'react';

const DRAFT_SAVE_DELAY = 1000; // 1 second
const DRAFT_KEY_PREFIX = 'thread_draft_';

export function useDraftSave(threadId, initialContent = '') {
  const [draft, setDraft] = useState(initialContent);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const timeoutRef = useRef(null);
  const draftKey = `${DRAFT_KEY_PREFIX}${threadId}`;

  // Load saved draft on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft && savedDraft !== initialContent) {
        setDraft(savedDraft);
      }
    } catch (error) {
      console.warn('Failed to load draft:', error);
    }
  }, [draftKey, initialContent]);

  // Auto-save function
  const saveDraft = useCallback(async (content) => {
    if (!content.trim()) {
      localStorage.removeItem(draftKey);
      return;
    }

    try {
      setIsAutoSaving(true);
      localStorage.setItem(draftKey, content);
      setLastSaved(new Date());
    } catch (error) {
      console.warn('Failed to save draft:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [draftKey]);

  // Debounced auto-save
  const debouncedSave = useCallback((content) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      saveDraft(content);
    }, DRAFT_SAVE_DELAY);
  }, [saveDraft]);

  // Update draft and trigger auto-save
  const updateDraft = useCallback((newContent) => {
    setDraft(newContent);
    debouncedSave(newContent);
  }, [debouncedSave]);

  // Clear draft
  const clearDraft = useCallback(() => {
    setDraft('');
    localStorage.removeItem(draftKey);
    setLastSaved(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [draftKey]);

  // Check if there's a saved draft different from initial content
  const hasDraft = useCallback(() => {
    try {
      const savedDraft = localStorage.getItem(draftKey);
      return savedDraft && savedDraft !== initialContent && savedDraft.trim();
    } catch {
      return false;
    }
  }, [draftKey, initialContent]);

  // Get all drafts for cleanup
  const getAllDrafts = useCallback(() => {
    const drafts = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(DRAFT_KEY_PREFIX)) {
          const threadId = key.replace(DRAFT_KEY_PREFIX, '');
          const content = localStorage.getItem(key);
          drafts.push({ threadId, content, key });
        }
      }
    } catch (error) {
      console.warn('Failed to get drafts:', error);
    }
    return drafts;
  }, []);

  // Cleanup old drafts
  const cleanupOldDrafts = useCallback((maxAge = 7 * 24 * 60 * 60 * 1000) => { // 7 days
    try {
      const drafts = getAllDrafts();
      const now = Date.now();
      
      drafts.forEach(({ key }) => {
        try {
          const savedTime = localStorage.getItem(`${key}_timestamp`);
          if (savedTime && now - parseInt(savedTime) > maxAge) {
            localStorage.removeItem(key);
            localStorage.removeItem(`${key}_timestamp`);
          }
        } catch {
          // If we can't read the timestamp, remove the draft
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to cleanup drafts:', error);
    }
  }, [getAllDrafts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Format last saved time
  const formatLastSaved = useCallback(() => {
    if (!lastSaved) return null;
    
    const now = new Date();
    const diff = now - lastSaved;
    
    if (diff < 60000) { // Less than 1 minute
      return 'Guardado hace un momento';
    } else if (diff < 3600000) { // Less than 1 hour
      const minutes = Math.floor(diff / 60000);
      return `Guardado hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    } else {
      return `Guardado a las ${lastSaved.toLocaleTimeString()}`;
    }
  }, [lastSaved]);

  return {
    draft,
    updateDraft,
    clearDraft,
    hasDraft,
    isAutoSaving,
    lastSaved,
    formatLastSaved,
    getAllDrafts,
    cleanupOldDrafts,
  };
}